<script setup lang="ts">
import { computed, ref, watch, onUpdated, onMounted } from 'vue';
import InputText from 'primevue/inputtext';
import ProgressSpinner from 'primevue/progressspinner';
import {
  ButtonType,
  Header,
  CustomButton,
  AccountAvatarInput,
  AccountAvatarCropDialog
} from '@/components';
import { BACKEND_URL } from '@/services/apiClient';
import { LocalStorageUtils, routeConstants } from '@/utils';
import { useStudySettingsStore } from '@/stores/studySettings';
import { useStudyStore } from '@/stores/study';
import { useAuthStore } from '@/stores/auth';
import { useUserManagementStore } from '@/stores/userManagement';
import Part11PinSetupDialog from './Part11PinSetupDialog.vue';
import { useRouter } from 'vue-router';

const openAvatarCropDialog = ref(false);

const openNewAvatarCropDialog = () => {
  openAvatarCropDialog.value = !openAvatarCropDialog.value;
};

const studySettingsStore = useStudySettingsStore();
studySettingsStore.getAllData();

const studyStore = useStudyStore();
const authStore = useAuthStore();
const userManagementStore = useUserManagementStore();

watch(
  () => studyStore.selectedStudy,
  (newId, oldId) => {
    if (oldId && newId !== oldId) {
      studySettingsStore.getAllData();
    }
  }
);

const patchUserDetails = async () => {
  await userManagementStore.patchUserDetails();
};
const formModified = computed(() => {
  return (
    LocalStorageUtils.getUserKeys().name !== authStore.userData?.name?.trim()
  );
});
const isFormDisabled = computed(() => {
  return !formModified.value || !authStore.userData?.name?.trim();
});
const computedAvatar = computed(() => {
  return `${BACKEND_URL}${authStore.userData?.avatar}`;
});

const avatarLoadError = ref(false);

const onAvatarError = () => {
  avatarLoadError.value = true;
};

const handleImageLoad = () => {
  avatarLoadError.value = false;
};

onMounted(() => {
  userManagementStore.getUserAvatar();
});

onUpdated(() => {
  if (authStore.userData?.avatar && avatarLoadError.value === true) {
    checkImageExists(computedAvatar.value);
  }
});

const checkImageExists = (imageUrl: string) => {
  const img = new Image();
  img.onload = () => {
    avatarLoadError.value = false;
  };
  img.onerror = () => {
    avatarLoadError.value = true;
  };
  img.src = imageUrl;
};

const revertChanges = () => {
  authStore.userData!.name = LocalStorageUtils.getUserKeys().name ?? '';
};

const router = useRouter();

const onPinSetupClick = () => {
  router.push({ name: routeConstants.PART_11_PIN_SETUP.name });
};

const closePinDialog = () => {
  router.push({ name: routeConstants.ACCOUNT_SETTINGS.name });
};
</script>

<template>
  <div class="user-settings">
    <div class="header">
      <Header title="Account Settings"></Header>
    </div>
    <div class="user-settings-container">
      <div v-if="studySettingsStore.isInitialLoadLoading"
        class="progress-container">
        <ProgressSpinner style="width: 32px" strokeWidth="4" />
      </div>
      <template v-if="!studySettingsStore.isInitialLoadLoading">
        <div class="main">
          <div class="user-name">
            <div class="user-name-label">User Name</div>
            <InputText v-model="authStore.userData!.name"
              class="user-name-input"
              placeholder="Enter your name"/>
            <div class="user-name-error">
              <span v-if="!authStore.userData?.name"
                severity="error">This field cannot be empty</span>
            </div>
          </div>
          <div class="user-name">
            <div class="user-name-label">Email address</div>
            <InputText v-model="authStore.userData!.email"
              class="user-name-input"
              placeholder="Enter the name of your study"
              disabled/>
            <div class="gray-span">
              <span>This is your username</span>
            </div>
          </div>
          <div class="buttons">
            <CustomButton text="Cancel" @onClick="revertChanges" />
            <CustomButton text="Save"
              :disabled="isFormDisabled"
              @onClick="patchUserDetails"
              :type="ButtonType.POSITIVE"
              :loading="
                userManagementStore.initialLoad.isLoading ||
                userManagementStore.avatarLoad.isLoading
              "/>
          </div>

          <CustomButton text="21 CFR Part 11 PIN Setup"
            @onClick="onPinSetupClick"
            v-if="studyStore.selectedStudy?.flags?.cfrPart11"/>
        </div>
        <div class="user-avatar">
          <div class="user-name-label">User Avatar</div>
          <img alt="User Avatar"
            :src="'data:image/jpeg;base64,' + userManagementStore.avatar"
            @error="onAvatarError"
            @load="handleImageLoad"/>
          <div v-if="avatarLoadError" class="user-avatar-error">
            <span>Something went wrong</span>
          </div>
          <AccountAvatarInput :openAvatarCropDialog="openAvatarCropDialog"
            @update:openAvatarCropDialog="openAvatarCropDialog = $event"/>
        </div>
      </template>
    </div>
    <AccountAvatarCropDialog :visible="openAvatarCropDialog"
      @onClose="openNewAvatarCropDialog"/>

    <RouterView v-slot="{ route }">
      <component v-if="route.name === routeConstants.PART_11_PIN_SETUP.name"
        :is="Part11PinSetupDialog"
        :visible="route.name === routeConstants.PART_11_PIN_SETUP.name"
        @onClose="closePinDialog"/>
    </RouterView>
  </div>
</template>

<style scoped lang="scss">
.user-settings {
  padding: 48px 100px 40px 80px;
}
.user-settings-container {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  align-items: flex-start;
  color: #272a2e;
  line-height: 20px;
  font-size: 14px;
  max-width: 900px;
  min-width: 600px;
  padding: 40px;
  border-width: 1px;
  border-color: #d9dee4;
  border-radius: 4px;
  background-color: #fff;
  box-shadow: 0 0 0 1px #ebeff2, 0 1px 10px 1px rgba(15, 15, 15, 0.05),
    0 1px 3px 1px rgba(15, 15, 15, 0.05);
  b {
    margin-bottom: 10px;
    font-weight: bold;
    font-size: 16px;
  }
  .progress-container {
    display: flex;
    align-items: center;
    justify-content: center;
    grid-column: span 2;
  }
}
.buttons {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 20px;
}
.user-avatar {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}
.user-avatar img {
  max-width: 140px;
  width: 100%;
  height: 140px;
  object-fit: cover;
  border-radius: 10%;
  border: 2px solid #ccc;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 10px;
}
.user-avatar-error {
  margin-bottom: 10px;
  color: #64748b;
}
.user-name {
  display: flex;
  flex-direction: column;
  margin: unset;

  input {
    max-width: 300px;
  }

  & + & {
    margin-top: 12px;
  }
}
.user-name-label {
  font-size: 16px;
  font-weight: 700;
  line-height: 30px;
}
.user-name-input {
  font-size: 14px;
}
.user-name-error {
  display: flex;
  justify-content: flex-start;
  span {
    color: red;
  }
}
</style>
